<template>
  <div>
    <div
      v-if="isLoading"
      class="loading"
    >
      <span class="fas fa-spinner fa-spin" />
    </div>

    <label class="tb-label">
      Season
    </label>

    <select
      v-model="seasonId"
      class="tb-select mb-2"
      data-test="producer-editor-sportradar-seasonDropdown"
      @change="handleSeasonChange"
      data-entity="producer-editor-selectSeason"
    >
      <option
        v-for="s in seasonOptions"
        :value="s.seasonId"
        :key="s.seasonId"
      >
        {{ s.seasonYear }} - {{ s.seasonTypeName || s.seasonTypeCode }}
      </option>
    </select>

    <label v-if="filterByConference" class="tb-label">
      Conference
    </label>

    <select
      v-if="filterByConference"
      v-model="conferenceFilter"
      class="tb-select mb-2"
      data-test="graphics-editor-sportradar-conferenceDropdown"
      @change="handleConferenceChange($event.target.value)"
      data-entity="producer-editor-selectConference"
    >
      <option :value="'all'">All Conferences</option>
      <option
        v-for="c in conferenceOptions"
        :value="c.name"
        :key="c.id"
      >
       {{c.name}}
      </option>
    </select>

    <label class="tb-label">
      Team
    </label>

    <select
      v-model="teamId"
      class="tb-select mb-2"
      data-entity="producer-editor-selectTeam"
      data-test="graphics-editor-sportradar-teamDropdown"
      @change="handleTeamChange"
    >
      <option
        v-for="t in displayTeams"
        :value="t.teamId"
        :key="t.teamId"
      >
        {{ t.teamDisplayName }}
      </option>
    </select>

    <template v-if="controlOptions.player">
      <label class="tb-label">
        Player
      </label>

      <select
        v-model="playerId"
        class="tb-select mb-2"
        data-entity="producer-editor-selectPlayer"
        data-test="graphics-editor-sportradar-playerDropdown"
      >

        <option
          v-for="p in playerOptions"
          :value="p.playerId"
          :key="p.playerId"
        >
          {{ playerDisplayName(p) }}
        </option>
      </select>
    </template>

    <template v-if="controlOptions.game">
      <label class="tb-label">
        Game
      </label>

      <select
        v-model="gameId"
        class="tb-select mb-2"
        data-entity="producer-editor-selectGame"
        data-test="graphics-editor-sportradar-gameDropdown"
      >
        <option
          v-for="g in gameOptions"
          :value="g.gameId"
          :key="g.gameId"
        >
          {{ eventDisplayName(g) }}
        </option>
      </select>
    </template>

    <hr />

    <template
      v-for="(el, idx) in mappedElements"
      :key="el.id"
    >
      <label class="tb-label">
        {{getElementLabelOverride(el.id) || `Stat ${idx + 1}` }}
      </label>

      <div class="mb-4 flex items-center">
        <div class="flex flex-grow items-center ellipsis mr-4" :data-test="`graphics-editor-sportradar-stat-${idx + 1}`">
          <template
            v-for="(crumb, idx) in fieldToLabel(getElementValue(el.id))"
            :key="`${crumb}:${idx}`"
          >
            <span
              v-if="idx > 0"
              class="fal fa-chevron-right sportradar-settings__path-arrow"
            />

            <span class="sportradar-settings__path-label">
              {{ createReadableLabel(crumb) }}
            </span>
          </template>
        </div>

        <button
          class="tb-btn tb-btn-contained medium-em"
          data-entity="producer-editor-showDataModal"
          :data-test="`graphics-editor-sportradar-selectData-${idx + 1}`"
          @click="showDataModal(el, idx)"
        >
          <span class="fas fa-database" />
        </button>
      </div>
    </template>

    <modal
      v-if="selectedElement !== null"
      class="tree-selector__modal"
      data-entity="producer-editor-closeDataModal"
      data-test="graphics-sportradar-dataModal-close"
      @close="closeDataModal"
    >
      <template #header>
        <div class="title">
          Update Data for {{ selectedElement.data?.labelOverride ? getElementLabelOverride(selectedElement.id) : `Stat ${selectedIdx + 1}` }}
        </div>

        <button
          class="modal-close tb-btn tb-btn-text sm medium-em"
          data-entity="producer-editor-closeDataModal"
          data-test="graphics-sportradar-dataModal-close"
          @click="closeDataModal"
        >
          <span class="fas fa-times" />
        </button>
      </template>

      <div class="sportradar-map-modal__body">
      <div class="mb-4">
          <input
            type="text"
            class="tb-input"
            placeholder="Search"
            v-model="search"
            data-entity="producer-editor-searchFields"
            data-test="graphics-sportradar-searchStats"
          />
        </div>

        <div class="sportradar-map-modal__scroll">
          <tree-selector
            v-if="Object.keys(filteredFlatSchema).length"
            :value="getElementValue(selectedElement.id)"
            :options="filteredFlatSchema"
            :label-mappings="labelMappings"
            :is-filtered="!!search.trim()"
            @select="saveAndCloseDataModal"
          />
          <p
            v-else
            class="text-center padding-md tgb-text-bold"
          >No results found</p>
        </div>
      </div>

      <template #footer>
        <button class="tb-btn-text" @click="closeDataModal">
          Cancel
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import flatten from 'flat';
import { get } from 'lodash-es';
import { playerDisplayName, eventDisplayName } from '../../../../utils/filters';

import Modal from '../../../modal/Modal.vue';
import TreeSelector from './TreeSelector.vue';
import { createReadableLabel } from '../../../../utils/helpers';

export default {
  name: 'SportradarSettings',
  components: {
    Modal,
    TreeSelector,
  },

  props: {
    element: {
      required: true,
      type: Object,
    },

    template: {
      required: true,
      type: Object,
    },

    graphic: {
      required: true,
      type: Object,
    },

    graphic_fields: {
      required: true,
      type: Object,
    },
  },

  data() {
    return {
      isLoading: false,
      selectedElement: null,
      displayTeams: [],
      selectedIdx: -1,
      search: '',
    };
  },

  computed: {
    ...mapState({
      rawSchema(state) {
        return state[this.element.id].rawSchema;
      },
      seasons(state) {
        return state[this.element.id].seasons;
      },
      Season(state) {
        return state[this.element.id].Season;
      },
      sport(state) {
        return state[this.element.id].sport;
      },
      teams(state) {
        return state[this.element.id].teams;
      },
      Team(state) {
        return state[this.element.id].Team;
      },
      Player(state) {
        return state[this.element.id].Player;
      },
      players(state) {
        return state[this.element.id].players;
      },
      Game(state) {
        return state[this.element.id].Game;
      },
      games(state) {
        return state[this.element.id].games;
      },
    }),

    playerSeasonStatsSchema() {
      return {
        Player: this.Player,
        Season: this.Season,
        Team: this.Team,
        PlayerSeasonStats: (this.usesSportradarSchema)
          ? {
            total: this.rawSchema[`PlayerSeasonStatsTotal${this.sport.toUpperCase()}`],
            average: this.rawSchema[`PlayerSeasonStatsAverage${this.sport.toUpperCase()}`],
          }
          : {
            statistics: this.rawSchema[`PlayerStatistics${this.sport.toUpperCase()}`],
          },
      };
    },

    teamSeasonStatsSchema() {
      return {
        Team: this.Team,
        Season: this.Season,
        TeamSeasonStats: (this.usesSportradarSchema)
          ? {
            opponentsTotal: this.rawSchema[`TeamSeasonStatsTotal${this.sport.toUpperCase()}`],
            opponentsAverage: this.rawSchema[`TeamSeasonStatsAverage${this.sport.toUpperCase()}`],
            teamTotal: this.rawSchema[`TeamSeasonStatsTotal${this.sport.toUpperCase()}`],
            teamAverage: this.rawSchema[`TeamSeasonStatsAverage${this.sport.toUpperCase()}`],
          }
          : {
            statistics: this.rawSchema[`TeamStatistics${this.sport.toUpperCase()}`],
          },
      };
    },

    teamGameStatsSchema() {
      return {
        Team: this.Team,
        Game: this.Game,
        TeamGameStats: {
          statistics: (this.usesSportradarSchema)
            ? this.rawSchema[`TeamGameStatistics${this.sport.toUpperCase()}`]
            : this.rawSchema[`TeamStatistics${this.sport.toUpperCase()}`],
        },
      };
    },

    playerGameStatsSchema() {
      return {
        Player: this.Player,
        Game: this.Game,
        Team: this.Team,
        PlayerGameStats: {
          statistics: (this.usesSportradarSchema)
            ? this.rawSchema[`PlayerGameStats${this.sport.toUpperCase()}`]
            : this.rawSchema[`PlayerStatistics${this.sport.toUpperCase()}`],
        },
      };
    },

    elementFields() {
      return this.graphic_fields[this.element.id];
    },

    sport() {
      return this.element.data.sport;
    },

    usesSportradarSchema() {
      // NOTE: update this list as we migrate leagues from SportRadar (legacy) to SportsStack
      const sportRadarSchemaLeagues = [
        'nba',
        'nfl',
        'nhl',
        'ncaafb',
        'ncaamb',
        'ncaawb',
      ];
      return sportRadarSchemaLeagues.includes(this.sport?.toLowerCase());
    },

    controlOptions() {
      return this.element.data?.controlOptions;
    },

    seasonLabel() {
      const season = this.Season;
      if (!season) {
        return 'Season';
      }

      return `${season.seasonYear} - ${season.seasonTypeName}`;
    },

    teamLabel() {
      const team = this.Team;
      return team?.teamName || 'Team';
    },

    playerLabel() {
      const player = this.Player;
      return player?.fullName || 'Player';
    },

    labelMappings() {
      return {
        Season: this.seasonLabel,
        Team: this.teamLabel,
        Player: this.playerLabel,
        opponentsTotal: 'Opponents Total',
        opponentsAverage: 'Opponents Average',
        teamTotal: 'Team Total',
        teamAverage: 'Team Average',
        total: 'Total',
        average: 'Average',
        PlayerGameStats: 'Player Game Stats',
        TeamGameStats: 'Team Game Stats',
        PlayerSeasonStats: 'Player Season Stats',
        TeamSeasonStats: 'Team Season Stats',
      };
    },

    templateType() {
      return this.element.data?.templateType || '';
    },

    seasonOptions() {
      // Sort by year, then type (alphabetized)
      return Object.values(this.seasons).sort((a, b) => {
        if (a.seasonYear === b.seasonYear) {
          return a.seasonTypeName < b.seasonTypeName ? -1 : 1;
        }

        return a.seasonYear > b.seasonYear ? -1 : 1;
      });
    },
    filterByConference() {
      const sportsWithConferences = ['ncaafb', 'ncaawb', 'ncaamb'];
      return sportsWithConferences.includes(this.sport);
    },
    conferenceOptions() {
      const teams = this.teamOptions;
      if (!teams?.length || !this.filterByConference) {
        return [];
      }

      // Dedupe conferences
      const conferenceMap = new Map();
      teams.forEach((team) => {
        const conference = team?.Conference;
        if (conference && !conferenceMap.has(conference.conferenceId)) {
          conferenceMap.set(conference.conferenceId, {
            name: conference.conferenceName,
            id: conference.conferenceId,
          });
        }
      });

      return Array.from(conferenceMap.values()).sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });
    },
    teamOptions() {
      // Alphabetize
      return Object.values(this.teams).sort((a, b) => (a.teamDisplayName < b.teamDisplayName ? -1 : 1));
    },

    playerOptions() {
      return Object.values(this.players).sort((a, b) => {
        const lastNameComparison = (a.lastName || '').localeCompare(b.lastName || '');
        if (lastNameComparison !== 0) {
          return lastNameComparison;
        }

        return (a.firstName || '').localeCompare(b.firstName || '');
      });
    },

    gameOptions() {
      if (this.sport?.startsWith('nascar')) {
        // Sort by date only
        return Object.values(this.games)
          .sort((a, b) => new Date(a?.scheduledFor) - new Date(b?.scheduledFor));
      }

      // Filter only to current team and sort by date
      return Object.values(this.games)
        .filter((g) => [g?.homeTeam?.teamName, g?.awayTeam?.teamName].includes(this.Team?.teamName))
        .sort((a, b) => new Date(a?.scheduledFor) - new Date(b?.scheduledFor));
    },

    seasonId: {
      get() {

        return this.Season?.seasonId;
      },
      set(seasonId) {
        this.setSeason(this.seasons[seasonId]);
        this.updateField('data.seasonId', seasonId);
      },
    },

    teamId: {
      get() {
        return this.Team.teamId;
      },

      set(teamId) {
        this.setTeam(this.teams[teamId]);
        this.updateField('data.teamId', teamId);
      },
    },

    playerId: {
      get() {
        return this.Player?.playerId || null;
      },
      set(playerId) {
        const player = this.players.find((p) => p.playerId === playerId);
        if (player) {
          this.setPlayer(player);  // Update the Player in state
          this.updateField('data.playerId', playerId);
        }
      },
    },

    gameId: {
      get() {
        return this.Game?.gameId;
      },

      set(gameId) {
        const game = this.games.find((g) => g.gameId === gameId);
        this.setGame(game);
        this.updateField('data.gameId', gameId);
      },
    },

    conferenceFilter: {
      get() {
        return this.$store.state[this.element.id].conferenceFilter || 'all';
      },
      set(filter) {
        this.setConferenceFilter(filter);
        this.updateField('data.conferenceFilter', filter);
      },
    },

    mappedElements() {
      const findMapped = (result, el) => {
        if (el.elements) {
          result.concat(el.elements.reduce(findMapped, result));
        }

        if (el.data?.provider === this.element.id) {
          // Ignore non-text mapped elements
          const { dataKeyMap } = el.data;
          if (!('text' in dataKeyMap)) {
            return result;
          }

          result.push(el);
        }

        return result;
      };

      let elements = this.element.elements.reduce(findMapped, []);

      // Filter out labels and only allow user to change label+value together
      // Very inefficient, but whatever (for now)

      elements = elements.reduce((memo, el) => {
        if (memo.find((e) => e.data.linkedElement === el.id)) {
          return memo;
        }

        memo.push(el);
        return memo;
      }, []);

      return elements.filter((el) => el?.data?.dataKeyMap?.text);
    },

    mappedElementsLookup() {
      return this.mappedElements.reduce((acc, el) => {
        acc[el.id] = el;
        return acc;
      }, {});
    },

    noRenderKeys() {
      return [
        // v1 Fields (API)
        'sr_id',
        'reference',
        'season_id',
        '_comment',
        'last_modified',
        'updated',
        // v2 Fields - GraphQL
        '__typename',
        'id',
        'leagueId',
        'seasonId',
        'conferenceId',
        'divisionId',
        'teamId',
        'homeTeamId',
        'awayTeamId',
        'playerId',
        'gameId',
        'scheduledForUnix',
        'venueId',
      ];
    },

    filteredFlatSchema() {
      const search = this.search.trim();
      const lowerCaseSearch = search.toLowerCase();

      const filteredAndFlat = Object.keys(this.flatSchema)
        .filter(key => {
          if (this.noRenderKeys.some(noRenderKey => key.includes(noRenderKey))) {
            return false;
          }

          // Search filter (with variants that replace underscores for QoL)
          return (
            key.includes(lowerCaseSearch) ||
            key.replace(/_/g, ' ').includes(lowerCaseSearch) ||
            key.replace(/_/g, '').includes(lowerCaseSearch)
          );
        })
        .reduce((memo, key) => ({
          ...memo,
          [key]: this.flatSchema[key],
        }), {});

      return filteredAndFlat;
    },

    relevantSchema() {
      const { player, team, season, game } = this.controlOptions;
      if (player && game) return this.playerGameStatsSchema;
      if (player && season) return this.playerSeasonStatsSchema;
      if (team && game) return this.teamGameStatsSchema;
      if (team && season) return this.teamSeasonStatsSchema;
      return {};
    },

    flatSchema() {
      return flatten(this.relevantSchema);
    },

    isValid() {
      // check if selected options exist in current data set
      let allValid = Object.values(this.teams).some((t) => t.teamId === this.teamId);

      if (this.controlOptions.player) {
        allValid = allValid && Object.values(this.players).some((p) => p.playerId === this.Player.playerId);
      }

      if (this.controlOptions.game) {
        allValid = allValid && Object.values(this.games).some((g) => g.gameId === this.Game?.gameId);
      }

      return allValid;
    },
  },

  watch: {
    Player(newPlayer) {
      // Update playerId in data when player OR team changes
      this.playerId = newPlayer.playerId;
    },

    Game(newGame) {
      if (newGame) {
        this.gameId = newGame.gameId;
      }
    },
  },

  methods: {
    playerDisplayName,
    eventDisplayName,
    createReadableLabel,

    ...mapActions({
      syncDataFromElement(dispatch, payload) {
        return dispatch(`${this.element.id}/syncDataFromElement`, payload);
      },
      statSelectorChange(dispatch, payload) {
        return dispatch(`${this.element.id}/statSelectorChange`, payload);
      },
      setSeason(dispatch, season) {
        return dispatch(`${this.element.id}/setSeason`, season);
      },
      setTeam(dispatch, team) {
        return dispatch(`${this.element.id}/setTeam`, team);
      },
      setPlayer(dispatch, player) {
        return dispatch(`${this.element.id}/setPlayer`, player);
      },
      setGame(dispatch, game) {
        return dispatch(`${this.element.id}/setGame`, game);
      },
      setConferenceFilter(dispatch, filter) {
        return dispatch(`${this.element.id}/setConferenceFilter`, filter);
      },
    }),

    updateField(field, value) {
      this.$emit('value', { field, value });
    },

    showDataModal(el, idx) {
      this.selectedElement = el;
      this.selectedIdx = idx;
    },

    saveAndCloseDataModal(val) {
      // Got change indices back to the mustache stuff
      // (no clean shaved key paths in this house)
      const key = val
        .replace('seasons.0', 'seasons.{{seasonId}}')
        .replace('teams.0', 'teams.{{teamId}}')
        .replace('players.0', 'players.{{playerId}}');
      this.updateMap(key, this.selectedElement.id);
      this.closeDataModal();
    },

    closeDataModal() {
      this.selectedElement = null;
      this.search = '';
    },

    fieldToLabel(field) {
      let parts = field.split('.');
      if (parts.length > 3) {
        // Simplify to last three with ellipsis before
        parts = parts.slice(-3);
        parts.unshift('...');
      }

      const label = parts.filter((part) => {
        if (!Number.isNaN(parseInt(part, 10))) {
          return false;
        }

        return true;
      });

      if (!this.seasonLabel) {
        return label;
      }

      // Pretty label map uses names/years/etc.
      const map = {
        // top level stats property names (graphQL resolvers)
        PlayerGameStats: 'Player Game Stats',
        TeamGameStats: 'Team Game Stats',
        PlayerSeasonStats: 'Player Season Stats',
        TeamSeasonStats: 'Team Season Stats',
      };
      return label.map((key) => map[key] ?? key);
    },

    async handleSeasonChange() {
      this.isLoading = true;
      await this._statSelectorChange('season'); // eslint-disable-line no-underscore-dangle

      // Update the teams list after season change
      if (this.filterByConference) {
        this.displayTeams = (this.conferenceFilter === 'all')
          ? this.teamOptions
          : this.teamOptions.filter((team) => team.Conference?.conferenceName === this.conferenceFilter);
      } else {
        this.displayTeams = this.teamOptions;
      }

      // Check if currently selected team is in the new list
      const currentTeamExists = this.displayTeams.some(team => team.teamId === this.teamId);
      if (!currentTeamExists && this.displayTeams.length > 0) {
        // Auto-select the first team in the dropdown
        this.teamId = this.displayTeams[0].teamId;
        // Trigger team change to update related team/player list data
        await this.handleTeamChange();
      }

      this.isLoading = false;
    },
    async handleConferenceChange(v) {
      this.isLoading = true;
      if (v !== 'all') {
        this.displayTeams = this.teamOptions.filter((team) => team.Conference?.conferenceName === v);
        this.teamId = this.displayTeams[0]?.teamId || this.teamOptions[0]?.teamId || null;
        await this.handleTeamChange();
      } else {
        this.displayTeams = this.teamOptions;
        // Check if currently selected team is in the new list
        const currentTeamExists = this.displayTeams.some(team => team.teamId === this.teamId);
        if (!currentTeamExists && this.displayTeams.length > 0) {
          // Auto-select the first team in the dropdown
          this.teamId = this.displayTeams[0].teamId;
          // Trigger team change to update related player/team list data
          await this.handleTeamChange();
        }
      }

      this.isLoading = false;
    },

    async handleTeamChange() {
      this.isLoading = true;
      await this._statSelectorChange('team'); // eslint-disable-line no-underscore-dangle
      this.isLoading = false;
    },
    async _statSelectorChange(selectionType) {
      let { teamId } = this;
      if (selectionType === 'season' && (!teamId || teamId === 'free_agent')) {
        // If no team is selected or the selected team is 'free_agent' choose the first team.
        teamId = Object.values(this.teams)[0].teamId;
      }

      try {
        await this.statSelectorChange({
          leagueName: this.sport,
          teamId,
          seasonId: this.seasonId,
          playerId: this.playerId,
          statPeriod: this.controlOptions.game ? 'game' : 'season',
          statType: this.controlOptions.player ? 'player' : 'team',
          selectionType,
        });
      } catch (error) {
        console.error('Error getting stats modal data', error);
      }
    },

    async syncData() {
      await this.syncDataFromElement(this.element.data);
    },

    getElementValue(id) {
      return get(this.graphic_fields[id], 'data.dataKeyMap.text')
        ?? this.mappedElementsLookup[id].data?.dataKeyMap.text;
    },
    getElementLabelOverride(id) {
      // this function is used to get the element label/name if it has been overriden
      if (!this.graphic_fields[id]?.data?.labelOverride && !this.mappedElementsLookup[id].data?.labelOverride) {
        return false;
      }

      return this.graphic_fields[id]?.name || this.mappedElementsLookup[id]?.name;
    },

    updateMap(e, id) {
      const selectedKey = typeof e === 'string' ? e : e.target.value;
      const elementFields = this.graphic_fields?.[id];
      if (!elementFields?.data?.dataKeyMap) {
        this.$emit('update-element', { id }, {
          field: 'data',
          value: {
            ...(this.elementFields?.data || {}),
            dataKeyMap: {},
          },
        });
      }

      this.$emit('update-element', { id }, {
        field: 'data',
        value: {
          ...this.elementFields.data,
          dataKeyMap: {
            ...this.elementFields.data.dataKeyMap,
            text: selectedKey,
          },
        },
      });

      // reset element label/name to default stat name after changing value
      this.resetElementLabel(id);

      // update linked element if any
      const { linkedElement } = this.mappedElementsLookup[id].data;
      if (linkedElement) {
        const linkedElementFields = this.graphic_fields[linkedElement];

        if (!linkedElementFields?.data) {
          this.$emit('update-element', { id: linkedElement }, {
            field: 'data',
            value: {
              dataKeyMap: {},
            },
          });
        }

        this.$emit('update-element', { id: linkedElement }, {
          field: 'data.dataKeyMap.text',
          value: selectedKey,
        });

        // reset element label/name to default stat name after changing value
        this.resetElementLabel(linkedElement);
      }
    },
    resetElementLabel(id) {
      // this function is used to reset the element label/name to the default stat name
      this.$emit('update-element', { id }, {
        field: 'name',
        value: `Stat ${this.mappedElements.findIndex(element => element.id === id) + 1}`, // reset name to index
      });

      // label is no longer overridden when element data is updated.
      this.$emit('update-element', { id }, {
        field: 'data.labelOverride',
        value: false,
      });
    },
  },

  async created() {
    if (!this.graphic_fields[this.element.id]?.data) {
      // Create empty data fields
      this.updateField('data', {});
    }

    this.isLoading = true;

    await this.syncData();
    // currently only ncaafb has conference filter
    if (this.filterByConference) {
      this.displayTeams = (this.conferenceFilter === 'all')
        ? this.teamOptions
        : this.teamOptions.filter((team) => team.Conference?.conferenceName === this.conferenceFilter);
    } else {
      this.displayTeams = this.teamOptions;
    }

    this.isLoading = false;
  },

  beforeUnmount() {
    if (!this.isValid) { // eslint-disable-line no-underscore-dangle
      this.errorToast('Incomplete Sportradar data selections, complete to avoid errors');
    }
  },

};
</script>

<style>

</style>
